import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, MapPin, Users, User, Clock } from "lucide-react";
import { proApi } from "@/lib/proApi";

// Farbpalette für Kategorien (deterministisch je Kategorie)
const PALETTE = [
  { dot: "#ff0033", bg: "rgba(255,0,51,0.14)", bd: "rgba(255,0,51,0.55)", tx: "#ff5c78" },
  { dot: "#3b82f6", bg: "rgba(59,130,246,0.14)", bd: "rgba(59,130,246,0.55)", tx: "#7cb0ff" },
  { dot: "#10b981", bg: "rgba(16,185,129,0.14)", bd: "rgba(16,185,129,0.55)", tx: "#5fe0b6" },
  { dot: "#f59e0b", bg: "rgba(245,158,11,0.14)", bd: "rgba(245,158,11,0.55)", tx: "#ffca5f" },
  { dot: "#a855f7", bg: "rgba(168,85,247,0.14)", bd: "rgba(168,85,247,0.55)", tx: "#c99cff" },
  { dot: "#06b6d4", bg: "rgba(6,182,212,0.14)", bd: "rgba(6,182,212,0.55)", tx: "#63d8ea" },
  { dot: "#ec4899", bg: "rgba(236,72,153,0.14)", bd: "rgba(236,72,153,0.55)", tx: "#ff8cc4" },
  { dot: "#84cc16", bg: "rgba(132,204,22,0.14)", bd: "rgba(132,204,22,0.55)", tx: "#bce85f" },
];
const colorFor = (cat) => {
  const key = (cat || "Kurs").toString();
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const WD = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => { const x = startOfDay(d); const dow = (x.getDay() + 6) % 7; return addDays(x, -dow); };
const hhmm = (iso) => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

const VIEWS = [["month", "Monat"], ["week", "Woche"], ["day", "Tag"], ["list", "Liste"]];

const ProCalendar = () => {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [cat, setCat] = useState("");
  const [coach, setCoach] = useState("");

  useEffect(() => { proApi.get("/pro/calendar").then((r) => setData(r.data)).catch(() => {}); }, []);

  const events = useMemo(() => {
    if (!data) return [];
    return (data.events || [])
      .filter((e) => e.start && e.status !== "cancelled")
      .filter((e) => !cat || e.category === cat)
      .filter((e) => !coach || e.coach === coach)
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [data, cat, coach]);

  const eventsByDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { const k = dayKey(new Date(e.start)); (m[k] = m[k] || []).push(e); });
    return m;
  }, [events]);

  const shift = (dir) => {
    if (view === "month") setCursor((c) => new Date(c.getFullYear(), c.getMonth() + dir, 1));
    else if (view === "week") setCursor((c) => addDays(c, dir * 7));
    else setCursor((c) => addDays(c, dir));
  };

  const periodLabel = () => {
    if (view === "month") return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (view === "week") { const s = mondayOf(cursor); const e = addDays(s, 6); return `${s.getDate()}.${s.getMonth() + 1}. – ${e.getDate()}.${e.getMonth() + 1}.${e.getFullYear()}`; }
    if (view === "day") return cursor.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    return "Kommende Termine";
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#ff0033]" /></div>;

  return (
    <div data-testid="pro-calendar">
      <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mb-5">Kalender</h1>

      {/* Steuerung */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex border border-white/10">
          {VIEWS.map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`pro-cal-view-${v}`}
              className={`px-4 py-2 text-xs font-heading uppercase tracking-wider transition-colors ${view === v ? "bg-[#ff0033] text-white" : "text-neutral-400 hover:text-white"}`}>{label}</button>
          ))}
        </div>
        {view !== "list" && (
          <div className="flex items-center gap-1">
            <button onClick={() => shift(-1)} data-testid="pro-cal-prev" className="p-2 border border-white/10 text-neutral-300 hover:border-[#ff0033] hover:text-white transition-colors"><ChevronLeft size={16} /></button>
            <button onClick={() => setCursor(new Date())} data-testid="pro-cal-today" className="px-3 py-2 border border-white/10 text-xs font-heading uppercase tracking-wider text-neutral-300 hover:border-[#ff0033] hover:text-white transition-colors">Heute</button>
            <button onClick={() => shift(1)} data-testid="pro-cal-next" className="p-2 border border-white/10 text-neutral-300 hover:border-[#ff0033] hover:text-white transition-colors"><ChevronRight size={16} /></button>
          </div>
        )}
        <span className="font-heading text-lg uppercase tracking-tight text-white ml-1" data-testid="pro-cal-period">{periodLabel()}</span>
        <div className="flex gap-2 ml-auto">
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="pro-cal-filter-category" className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#ff0033] outline-none">
            <option value="">Alle Kategorien</option>
            {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={coach} onChange={(e) => setCoach(e.target.value)} data-testid="pro-cal-filter-coach" className="bg-[#1A1A1A] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#ff0033] outline-none">
            <option value="">Alle Trainer</option>
            {(data.coaches || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      {/* Legende */}
      {(data.categories || []).length > 0 && (
        <div className="flex flex-wrap gap-3 mb-5">
          {data.categories.map((c) => { const col = colorFor(c); return (
            <span key={c} className="flex items-center gap-1.5 text-[11px] text-neutral-400"><span className="w-2.5 h-2.5 rounded-full" style={{ background: col.dot }} /> {c}</span>
          ); })}
        </div>
      )}

      {view === "month" && <MonthView cursor={cursor} eventsByDay={eventsByDay} nav={nav} onDay={(d) => { setCursor(d); setView("day"); }} />}
      {view === "week" && <WeekView cursor={cursor} eventsByDay={eventsByDay} nav={nav} />}
      {view === "day" && <DayView cursor={cursor} events={(eventsByDay[dayKey(cursor)] || [])} nav={nav} />}
      {view === "list" && <ListView events={events} nav={nav} />}
    </div>
  );
};

const EventChip = ({ e, nav, compact }) => {
  const col = colorFor(e.category);
  return (
    <button onClick={() => nav(`/pro/courses/${e.course_id}`)} data-testid={`pro-cal-event-${e.session_id}`}
      className="w-full text-left rounded px-1.5 py-1 mb-1 truncate transition-transform hover:scale-[1.02]"
      style={{ background: col.bg, borderLeft: `3px solid ${col.dot}` }} title={`${e.name} · ${hhmm(e.start)}${e.end ? "–" + hhmm(e.end) : ""}`}>
      <span className="text-[10px] font-mono-lab" style={{ color: col.tx }}>{hhmm(e.start)}</span>
      <span className={`block text-white leading-tight ${compact ? "text-[11px] truncate" : "text-xs"}`}>{e.name}</span>
    </button>
  );
};

const MonthView = ({ cursor, eventsByDay, nav, onDay }) => {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = mondayOf(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const todayKey = dayKey(new Date());
  return (
    <div className="border border-white/10" data-testid="pro-cal-month">
      <div className="grid grid-cols-7 bg-[#0F0F0F] border-b border-white/10">
        {WD.map((d) => <div key={d} className="px-2 py-2 text-center font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const evs = eventsByDay[dayKey(d)] || [];
          return (
            <div key={i} onClick={() => onDay(d)} className={`min-h-[104px] border-b border-r border-white/5 p-1.5 cursor-pointer hover:bg-white/[0.03] transition-colors ${inMonth ? "" : "opacity-35"}`}>
              <div className={`text-xs mb-1 flex items-center justify-center w-6 h-6 rounded-full ${dayKey(d) === todayKey ? "bg-[#ff0033] text-white font-bold" : "text-neutral-400"}`}>{d.getDate()}</div>
              {evs.slice(0, 3).map((e) => <EventChip key={e.session_id} e={e} nav={nav} compact />)}
              {evs.length > 3 && <span className="text-[10px] text-neutral-500 pl-1">+{evs.length - 3} weitere</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WeekView = ({ cursor, eventsByDay, nav }) => {
  const start = mondayOf(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const todayKey = dayKey(new Date());
  return (
    <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="pro-cal-week">
      {days.map((d, i) => {
        const evs = eventsByDay[dayKey(d)] || [];
        return (
          <div key={i} className="bg-[#0F0F0F] border border-white/10 p-2 min-h-[160px]">
            <div className={`text-center mb-2 pb-2 border-b border-white/5 ${dayKey(d) === todayKey ? "text-[#ff0033]" : "text-neutral-400"}`}>
              <div className="font-mono-lab text-[10px] uppercase tracking-widest">{WD[i]}</div>
              <div className="font-heading text-lg">{d.getDate()}.{d.getMonth() + 1}.</div>
            </div>
            {evs.length === 0 ? <p className="text-[10px] text-neutral-600 text-center mt-4">—</p> : evs.map((e) => <EventChip key={e.session_id} e={e} nav={nav} />)}
          </div>
        );
      })}
    </div>
  );
};

const DetailRow = ({ e, nav }) => {
  const col = colorFor(e.category);
  return (
    <button onClick={() => nav(`/pro/courses/${e.course_id}`)} data-testid={`pro-cal-event-${e.session_id}`}
      className="w-full text-left bg-[#0F0F0F] border border-white/10 hover:border-[#ff0033]/50 transition-colors p-4 flex items-start gap-3">
      <span className="w-1 self-stretch rounded" style={{ background: col.dot }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-lab text-[9px] uppercase tracking-widest px-2 py-0.5 border" style={{ color: col.tx, borderColor: col.bd }}>{e.category || "Kurs"}</span>
          <h3 className="font-heading text-lg uppercase tracking-tight text-white leading-tight">{e.name}</h3>
        </div>
        <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-neutral-400">
          <span className="flex items-center gap-2"><Clock size={13} className="text-[#ff0033]" /> {hhmm(e.start)}{e.end ? `–${hhmm(e.end)} Uhr` : " Uhr"}</span>
          <span className="flex items-center gap-2"><MapPin size={13} className="text-[#ff0033]" /> {e.location || "—"}</span>
          <span className="flex items-center gap-2"><User size={13} className="text-[#ff0033]" /> {e.coach || "—"}</span>
          <span className="flex items-center gap-2"><Users size={13} className="text-[#ff0033]" /> {e.booked} Teilnehmer{e.capacity ? ` / ${e.capacity}` : ""}</span>
        </div>
        {e.title && <p className="text-[#ff0033] text-xs mt-2">Inhalt: {e.title}</p>}
      </div>
    </button>
  );
};

const DayView = ({ cursor, events, nav }) => (
  <div className="space-y-3" data-testid="pro-cal-day">
    {events.length === 0 ? <p className="text-neutral-500 py-10 text-center">Keine Termine an diesem Tag.</p>
      : events.map((e) => <DetailRow key={e.session_id} e={e} nav={nav} />)}
  </div>
);

const ListView = ({ events, nav }) => {
  const now = new Date();
  const upcoming = events.filter((e) => new Date(e.start) >= startOfDay(now));
  const groups = {};
  upcoming.forEach((e) => { const k = new Date(e.start).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }); (groups[k] = groups[k] || []).push(e); });
  const keys = Object.keys(groups);
  return (
    <div className="space-y-6" data-testid="pro-cal-list">
      {keys.length === 0 ? <p className="text-neutral-500 py-10 text-center">Keine kommenden Termine.</p>
        : keys.map((k) => (
          <div key={k}>
            <h2 className="font-mono-lab text-[11px] uppercase tracking-[0.25em] text-[#ff0033] mb-3">{k}</h2>
            <div className="space-y-3">{groups[k].map((e) => <DetailRow key={e.session_id} e={e} nav={nav} />)}</div>
          </div>
        ))}
    </div>
  );
};

export default ProCalendar;
